<template>
    <section class="relative isolate overflow-hidden bg-white px-6 py-16 sm:py-24 lg:px-8">
        <div class="pointer-events-none absolute top-1/4 -left-24 -z-10 h-72 w-72 rounded-full bg-primary-100 opacity-60 blur-3xl"
            aria-hidden="true"></div>
        <div class="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div class="text-center">
                <span
                    class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-4 py-1.5 text-sm font-semibold text-primary-700 ring-1 ring-primary-600/10">FAQs</span>
                <h2 class="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                    Looking for answers?
                </h2>
            </div>
            <div class="mt-10 space-y-4 text-left" data-accordion="default-accordion">
                <div v-for="faq in faqs" :key="faq.id"
                    class="rounded-2xl bg-white p-2 ring-1 ring-slate-900/5 shadow-sm transition-shadow duration-300"
                    :class="faq.active ? 'shadow-md' : ''" :id="`basic-heading-${faq.id}`">
                    <button
                        class="accordion-toggle group inline-flex items-center justify-between text-lg font-normal leading-8 w-full px-4 py-3 transition duration-300 hover:text-primary-600"
                        :class="faq.active ? 'text-primary-600 font-medium' : 'text-slate-900'"
                        :aria-controls="`basic-collapse-${faq.id}`" :aria-expanded="faq.active"
                        @click="toggleAccordion(faq)">
                        <h5 class="text-left">{{ faq.question }}</h5>
                        <svg class="shrink-0 ml-4 transition-transform duration-300"
                            :class="faq.active ? 'rotate-180 text-primary-600' : 'rotate-0 text-slate-500'"
                            width="20" height="20" viewBox="0 0 22 22" fill="none"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M16.5 8.25L12.4142 12.3358C11.7475 13.0025 11.4142 13.3358 11 13.3358C10.5858 13.3358 10.2525 13.0025 9.58579 12.3358L5.5 8.25"
                                stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
                                stroke-linejoin="round"></path>
                        </svg>
                    </button>
                    <transition @before-enter="beforeEnter" @enter="enter" @before-leave="beforeLeave"
                        @leave="leave">
                        <div :id="`basic-collapse-${faq.id}`" v-show="faq.active"
                            class="accordion-content w-full overflow-hidden px-4 pb-4"
                            :aria-labelledby="`basic-heading-${faq.id}`">
                            <p class="text-base font-normal text-slate-600">
                                {{ faq.answer }}
                            </p>
                        </div>
                    </transition>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup type="module">

import { ref } from 'vue';
import { getFaqs } from '@/services/faqService';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { FileUpload } from 'primevue';

const faqs = ref([]);
const email = ref('');
const resume = ref(null);
const errors = ref({ email: '', resume: '' });

const validateEmail = (value) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(value);
};

const allowedFileTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const maxFileSize = 2 * 1024 * 1024; // 2MB

const fetchFaqs = async () => {
    try {
        const response = await getFaqs();
        faqs.value = response.data;
    } catch (error) {
        console.error('Error fetching FAQs:', error);
    }
};

fetchFaqs();

const toggleAccordion = (faq) => {
    faqs.value.forEach((item) => {
        item.active = item.id === faq.id ? !item.active : false;
    });
};

// Transition hooks for height animation
const beforeEnter = (el) => {
    el.style.height = '0';
};

const enter = (el) => {
    el.style.transition = 'height 0.3s ease';
    el.style.height = el.scrollHeight + 'px';
};

const beforeLeave = (el) => {
    el.style.height = el.scrollHeight + 'px';
};

const leave = (el) => {
    el.style.transition = 'height 0.3s ease';
    el.offsetHeight; // force reflow
    el.style.height = '0';
};

</script>

<style scoped>
.p-fileupload.p-fileupload-advanced.p-component {
    width: 100%;
}
</style>